/**
 * WhatsApp Templates API Service
 *
 * Service for interacting with WhatsApp template management REST API
 */

import axios from 'axios';
import {
  WhatsAppTemplate,
  TemplateCreateRequest,
  TemplateUpdateRequest,
  TemplateSendRequest,
  TemplateListResponse,
  TemplateResponse,
  TemplatePreviewResponse,
  TemplateSequence,
  SequenceCreateRequest,
  SequenceUpdateRequest,
  SequenceStartRequest,
  SequenceListResponse,
  SequenceResponse,
  SequenceStats,
  SessionSummary,
  SessionResponse,
  WhatsAppConstraints,
  SessionRules,
  ApiResponse,
  ContactSequenceState
} from '../types/whatsapp-templates';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001/api/v1';

// Create axios instance with auth
const createApiClient = () => {
  const client = axios.create({
    baseURL: `${API_BASE_URL}/whatsapp`,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Add auth token interceptor
  client.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  return client;
};

const api = createApiClient();

// ===================================================================
// TEMPLATE CRUD API
// ===================================================================

export const whatsappTemplatesApi = {
  /**
   * Create a new WhatsApp template
   */
  createTemplate: async (
    accountId: number,
    templateData: TemplateCreateRequest,
    createdByUserId?: number
  ): Promise<TemplateResponse> => {
    try {
      const response = await api.post<TemplateResponse>('/templates', templateData, {
        params: { account_id: accountId, created_by_user_id: createdByUserId },
      });
      return response.data;
    } catch (error: any) {
      // Extract error detail from API response and re-throw with proper message
      const errorDetail = error?.response?.data?.detail || error?.message || 'Failed to create template';
      console.error('API Error creating template:', errorDetail);
      const newError = new Error(errorDetail);
      (newError as any).response = error.response; // Preserve response for additional context
      throw newError;
    }
  },

  /**
   * List all templates for an account
   */
  listTemplates: async (
    accountId: number,
    filters?: {
      status?: string;
      category?: string;
      language?: string;
      search?: string;
      active_only?: boolean;
    }
  ): Promise<TemplateListResponse> => {
    const response = await api.get<TemplateListResponse>('/templates', {
      params: { account_id: accountId, ...filters },
    });
    return response.data;
  },

  /**
   * Get a specific template by ID
   */
  getTemplate: async (templateId: number): Promise<TemplateResponse> => {
    const response = await api.get<TemplateResponse>(`/templates/${templateId}`);
    return response.data;
  },

  /**
   * Update a template
   */
  updateTemplate: async (
    templateId: number,
    updates: TemplateUpdateRequest
  ): Promise<TemplateResponse> => {
    try {
      const response = await api.put<TemplateResponse>(`/templates/${templateId}`, updates);
      return response.data;
    } catch (error: any) {
      const errorDetail = error?.response?.data?.detail || error?.message || 'Failed to update template';
      console.error('API Error updating template:', errorDetail);
      const newError = new Error(errorDetail);
      (newError as any).response = error.response;
      throw newError;
    }
  },

  /**
   * Delete a template (soft delete)
   */
  deleteTemplate: async (templateId: number): Promise<ApiResponse<void>> => {
    const response = await api.delete<ApiResponse<void>>(`/templates/${templateId}`);
    return response.data;
  },

  // ===================================================================
  // TEMPLATE APPROVAL API
  // ===================================================================

  /**
   * Submit template to provider for approval
   */
  submitForApproval: async (
    templateId: number,
    channelId: number
  ): Promise<ApiResponse<any>> => {
    const response = await api.post<ApiResponse<any>>(`/templates/${templateId}/submit`, null, {
      params: { channel_id: channelId },
    });
    return response.data;
  },

  /**
   * Sync template status from provider
   */
  syncTemplateStatus: async (
    templateId: number,
    channelId: number
  ): Promise<TemplateResponse> => {
    const response = await api.post<TemplateResponse>(`/templates/${templateId}/sync`, null, {
      params: { channel_id: channelId },
    });
    return response.data;
  },

  /**
   * Sync all templates from provider
   */
  syncAllTemplates: async (channelId: number): Promise<TemplateListResponse> => {
    const response = await api.post<TemplateListResponse>('/templates/sync-all', null, {
      params: { channel_id: channelId },
    });
    return response.data;
  },

  /**
   * List template names (and language, status) that exist on the WhatsApp provider.
   * Use to see which names are already in use so you can create with a new name.
   */
  getTemplateNamesOnProvider: async (
    channelId: number
  ): Promise<{ success: boolean; templates: Array<{ name: string; language: string; status: string }>; count: number }> => {
    const response = await api.get<{ success: boolean; templates: Array<{ name: string; language: string; status: string }>; count: number }>(
      '/templates/names-on-provider',
      { params: { channel_id: channelId } }
    );
    return response.data;
  },

  /**
   * Check if a template name is available on the WhatsApp provider (unique for submission).
   * When editing, pass excludeTemplateId so the current template is not marked in use.
   */
  checkTemplateNameAvailable: async (
    channelId: number,
    name: string,
    language: string = 'en',
    excludeTemplateId?: number
  ): Promise<{ success: boolean; available: boolean; message: string }> => {
    const params: Record<string, string | number> = {
      channel_id: channelId,
      name: (name || '').trim(),
      language: (language || 'en').trim(),
    };
    if (excludeTemplateId != null) params.exclude_template_id = excludeTemplateId;
    const response = await api.get<{ success: boolean; available: boolean; message: string }>(
      '/templates/check-name',
      { params }
    );
    return response.data;
  },

  // ===================================================================
  // TEMPLATE SENDING API
  // ===================================================================

  /**
   * Send a template to a contact
   */
  sendTemplate: async (
    templateId: number,
    sendData: TemplateSendRequest
  ): Promise<ApiResponse<any>> => {
    const response = await api.post<ApiResponse<any>>(`/templates/${templateId}/send`, sendData);
    return response.data;
  },

  /**
   * Preview a template with variables
   */
  previewTemplate: async (
    templateId: number,
    variables?: Record<string, any>
  ): Promise<TemplatePreviewResponse> => {
    const response = await api.post<TemplatePreviewResponse>(
      `/templates/${templateId}/preview`,
      variables || {}
    );
    return response.data;
  },

  // ===================================================================
  // SESSION MANAGEMENT API
  // ===================================================================

  /**
   * Get session status for a conversation
   */
  getSessionStatus: async (conversationId: number): Promise<SessionResponse> => {
    const response = await api.get<SessionResponse>(`/sessions/${conversationId}`);
    return response.data;
  },

  /**
   * Manually close a session
   */
  closeSession: async (conversationId: number): Promise<ApiResponse<any>> => {
    const response = await api.post<ApiResponse<any>>(`/sessions/${conversationId}/close`);
    return response.data;
  },

  // ===================================================================
  // SEQUENCE MANAGEMENT API
  // ===================================================================

  /**
   * Create a new template sequence
   */
  createSequence: async (
    accountId: number,
    sequenceData: SequenceCreateRequest,
    createdByUserId?: number
  ): Promise<SequenceResponse> => {
    const response = await api.post<SequenceResponse>('/sequences', sequenceData, {
      params: { account_id: accountId, created_by_user_id: createdByUserId },
    });
    return response.data;
  },

  /**
   * List all sequences for an account
   */
  listSequences: async (
    accountId: number,
    activeOnly: boolean = false
  ): Promise<SequenceListResponse> => {
    const response = await api.get<SequenceListResponse>('/sequences', {
      params: { account_id: accountId, active_only: activeOnly },
    });
    return response.data;
  },

  /**
   * Get a specific sequence by ID
   */
  getSequence: async (sequenceId: number): Promise<SequenceResponse> => {
    const response = await api.get<SequenceResponse>(`/sequences/${sequenceId}`);
    return response.data;
  },

  /**
   * Update a sequence
   */
  updateSequence: async (
    sequenceId: number,
    updates: SequenceUpdateRequest
  ): Promise<SequenceResponse> => {
    const response = await api.put<SequenceResponse>(`/sequences/${sequenceId}`, updates);
    return response.data;
  },

  /**
   * Delete a sequence
   */
  deleteSequence: async (sequenceId: number): Promise<ApiResponse<void>> => {
    const response = await api.delete<ApiResponse<void>>(`/sequences/${sequenceId}`);
    return response.data;
  },

  /**
   * Start a sequence for a contact
   */
  startSequence: async (
    sequenceId: number,
    startData: SequenceStartRequest
  ): Promise<ApiResponse<ContactSequenceState>> => {
    const response = await api.post<ApiResponse<ContactSequenceState>>(
      `/sequences/${sequenceId}/start`,
      startData
    );
    return response.data;
  },

  /**
   * Stop a running sequence
   */
  stopSequence: async (stateId: number, reason?: string): Promise<ApiResponse<any>> => {
    const response = await api.post<ApiResponse<any>>(
      `/sequences/states/${stateId}/stop`,
      { reason }
    );
    return response.data;
  },

  /**
   * Get sequence statistics
   */
  getSequenceStats: async (sequenceId: number): Promise<ApiResponse<SequenceStats>> => {
    const response = await api.get<ApiResponse<SequenceStats>>(`/sequences/${sequenceId}/stats`);
    return response.data;
  },

  // ===================================================================
  // CONFIGURATION API
  // ===================================================================

  /**
   * Get WhatsApp platform constraints
   */
  getWhatsAppConstraints: async (accountId: number = 1): Promise<ApiResponse<WhatsAppConstraints>> => {
    const response = await api.get<ApiResponse<WhatsAppConstraints>>('/config/constraints', {
      params: { account_id: accountId },
    });
    return response.data;
  },

  /**
   * Get session management rules
   */
  getSessionRules: async (accountId: number = 1): Promise<ApiResponse<SessionRules>> => {
    const response = await api.get<ApiResponse<SessionRules>>('/config/session-rules', {
      params: { account_id: accountId },
    });
    return response.data;
  },
};

export default whatsappTemplatesApi;
